import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Users, Activity, Eye, EyeOff, Building2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useUser } from '../context/UserContext';
import { PrivacyStatementModal, PRIVACY_STATEMENT_VERSION } from '../components/legal/PrivacyStatement';

// Theme Hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('mygoal-theme');
    return saved ? saved === 'dark' : true;
  });
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('mygoal-theme', newTheme ? 'dark' : 'light');
  };
  return { isDark, toggleTheme };
};

// Theme classes helper
const useThemeClasses = (isDark) => ({
  bg: isDark ? 'bg-[#0a0a0f]' : 'bg-gradient-to-br from-[#f8f7ff] via-[#f0eeff] to-[#e8e4ff]',
  card: isDark ? 'bg-[#1a1a2e]/80 border-white/10' : 'bg-white/80 border-[#493986]/10 shadow-lg',
  textPrimary: isDark ? 'text-white' : 'text-[#493986]',
  textSecondary: isDark ? 'text-white/60' : 'text-[#493986]/60',
  textMuted: isDark ? 'text-white/50' : 'text-[#493986]/50',
  input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-white border-[#493986]/20 text-[#493986] placeholder-[#493986]/30',
  button: isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-[#493986]/5 hover:bg-[#493986]/10',
});

export const AccountSelectionPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { createAccount, user } = useUser();
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [experience, setExperience] = useState('');
  const [clubName, setClubName] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showOrgContactPopup, setShowOrgContactPopup] = useState(false);
  const orgContactEmail = 'support@mygoalapp.com';

  // If user already has account type, redirect to home
  useEffect(() => {
    if (user?.account_type) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleClubSelectionClick = () => {
    setShowOrgContactPopup(true);
  };

  const handleCopyOrgEmail = async () => {
    try {
      await navigator.clipboard.writeText(orgContactEmail);
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy email");
    }
  };

  const handleCreateAccount = async () => {
    if (saving) return;
    if (!privacyAccepted) { toast.error('Please read and agree to the Privacy Statement before creating an account.'); return; }
    if (!selectedType || !name.trim()) {
      toast.error("Please select account type and enter your name");
      return;
    }

    if (selectedType === 'club') {
      setShowOrgContactPopup(true);
      return;
    }

    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    if (!password) {
      toast.error("Please enter a password");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await createAccount({
        name: name.trim(),
        email: email.trim(),
        password: password,
        account_type: selectedType,
        privacy_accepted: privacyAccepted,
        privacy_statement_version: PRIVACY_STATEMENT_VERSION,
        specialty: selectedType === 'coach' ? specialty : null,
        experience: selectedType === 'coach' ? experience : null,
        club_name: selectedType === 'club' ? (clubName.trim() || `${name.trim()}'s Club`) : null
      });
      toast.success(`${selectedType === 'coach' ? 'Coach' : selectedType === 'club' ? 'Club' : 'Athlete'} account created!`);
      navigate(selectedType === 'club' ? '/club' : '/');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col p-4`} data-testid="page-account-selection">
      {/* Top bar with back-to-home */}
      <header className="flex items-center justify-between mb-2">
        <button
          onClick={() => navigate('/')}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.card}`}
          data-testid="register-back-home-btn"
          aria-label="Back to home"
        >
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <button
          onClick={() => navigate('/')}
          className={`text-sm px-3 py-1.5 rounded-full transition-colors ${theme.textMuted} hover:${theme.textPrimary}`}
          data-testid="register-back-home-text-btn"
        >
          Home
        </button>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
        {/* Logo */}
        <div 
          className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6"
          style={{
            background: 'linear-gradient(135deg, #493986 0%, #6b5b9a 50%, #92a7e8 100%)',
            boxShadow: '0 20px 40px -10px rgba(73, 57, 134, 0.6)'
          }}
        >
          <Target className="w-10 h-10 text-white" />
        </div>
        
        <h1 className={`text-2xl font-bold mb-2 ${theme.textPrimary}`}>Welcome to MyGoal</h1>
        <p className={`text-center mb-8 ${theme.textMuted}`}>Choose your account type to get started</p>

        {!selectedType ? (
          <>
            {/* Account Type Selection */}
            <div className="grid grid-cols-2 gap-4 w-full mb-4">
              <button
                onClick={() => setSelectedType('athlete')}
                className={`p-6 rounded-2xl border backdrop-blur-xl transition-all ${theme.card} hover:scale-[1.02]`}
                data-testid="select-athlete"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center mx-auto mb-3">
                  <Activity className={`w-7 h-7 ${theme.textPrimary}`} />
                </div>
                <h3 className={`font-bold ${theme.textPrimary}`}>Athlete</h3>
                <p className={`text-xs mt-1 ${theme.textMuted}`}>Track training & goals</p>
              </button>

              <button
                onClick={() => setSelectedType('coach')}
                className={`p-6 rounded-2xl border backdrop-blur-xl transition-all ${theme.card} hover:scale-[1.02]`}
                data-testid="select-coach"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center mx-auto mb-3">
                  <Users className={`w-7 h-7 ${theme.textPrimary}`} />
                </div>
                <h3 className={`font-bold ${theme.textPrimary}`}>Coach</h3>
                <p className={`text-xs mt-1 ${theme.textMuted}`}>Manage your team</p>
              </button>
            </div>
            
            {/* Club Account Option */}
            <button
              onClick={handleClubSelectionClick}
              className={`w-full p-4 rounded-2xl border backdrop-blur-xl transition-all ${theme.card} hover:scale-[1.01] mb-6`}
              data-testid="select-club"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                  <Building2 className={`w-6 h-6 ${theme.textPrimary}`} />
                </div>
                <div className="text-left">
                  <h3 className={`font-bold ${theme.textPrimary}`}>Club / Organization</h3>
                  <p className={`text-xs ${theme.textMuted}`}>Manage multiple athletes & coaches</p>
                </div>
              </div>
            </button>
          </>
        ) : (
          <>
            {/* Account Creation Form */}
            <div className={`w-full p-6 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
                  {selectedType === 'coach' ? (
                    <Users className="w-6 h-6 text-white" />
                  ) : selectedType === 'club' ? (
                    <Building2 className="w-6 h-6 text-white" />
                  ) : (
                    <Activity className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className={`font-bold ${theme.textPrimary}`}>
                    {selectedType === 'coach' ? 'Coach Account' : selectedType === 'club' ? 'Club Account' : 'Athlete Account'}
                  </h3>
                  <button 
                    disabled={saving}
                    onClick={() => setSelectedType(null)}
                    className={`text-xs ${theme.textMuted} hover:underline`}
                  >
                    Change selection
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Name *</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                    data-testid="account-name-input"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Email *</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                    data-testid="account-email-input"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input} pr-12`}
                      data-testid="account-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Confirm Password *</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                    data-testid="account-confirm-password-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                  {confirmPassword && password === confirmPassword && password.length >= 6 && (
                    <p className="text-green-500 text-xs mt-1">Passwords match ✓</p>
                  )}
                </div>

                {selectedType === 'coach' && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Specialty</label>
                      <select
                        value={specialty}
                        onChange={(e) => setSpecialty(e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                        data-testid="coach-specialty-select"
                      >
                        <option value="">Select specialty</option>
                        <option value="Technical Skills">Technical Skills</option>
                        <option value="Physical Conditioning">Physical Conditioning</option>
                        <option value="Tactical Analysis">Tactical Analysis</option>
                        <option value="Mental Performance">Mental Performance</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Experience</label>
                      <input
                        type="text"
                        placeholder="e.g., 5 years"
                        value={experience}
                        onChange={(e) => setExperience(e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                        data-testid="coach-experience-input"
                      />
                    </div>
                  </>
                )}

                {selectedType === 'club' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Club / Organization Name</label>
                    <input
                      type="text"
                      placeholder="e.g., FC MyGoal Academy"
                      value={clubName}
                      onChange={(e) => setClubName(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                      data-testid="club-name-input"
                    />
                  </div>
                )}
              </div>

              <div className={`mt-6 rounded-xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-[#493986]/15 bg-[#493986]/5'}`}>
                <label htmlFor="account-privacy-consent" className={`flex cursor-pointer items-start gap-3 text-sm leading-6 ${theme.textSecondary}`}>
                  <input
                    id="account-privacy-consent"
                    data-testid="account-privacy-consent"
                    type="checkbox"
                    required
                    checked={privacyAccepted}
                    disabled={saving}
                    aria-describedby="account-privacy-help"
                    onChange={event => setPrivacyAccepted(event.target.checked)}
                    className="mt-1 h-5 w-5 shrink-0 accent-[#493986]"
                  />
                  <span>I have read and agree to the{' '}
                    <a href="/privacy" data-testid="account-privacy-link" className={`font-semibold underline underline-offset-2 ${theme.textPrimary}`} onClick={event => {
                      if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); setShowPrivacy(true); }
                    }}>Privacy Statement</a>.
                  </span>
                </label>
                <p id="account-privacy-help" className={`mt-2 text-xs ${theme.textMuted}`}>Required to create your account.</p>
              </div>

              <button
                onClick={handleCreateAccount}
                disabled={saving || !privacyAccepted}
                className="w-full mt-6 py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50"
                data-testid="create-account-button"
              >
                {saving ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </>
        )}
      </div>

      <PrivacyStatementModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />

      {showOrgContactPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="organization-contact-popup">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowOrgContactPopup(false)} />
          <div className={`relative w-full max-w-sm rounded-2xl border p-5 ${theme.card}`}>
            <h3 className={`text-lg font-bold mb-2 ${theme.textPrimary}`} data-testid="organization-contact-title">Organization Account</h3>
            <p className={`text-sm mb-4 ${theme.textSecondary}`} data-testid="organization-contact-message">
              For organization accounts, please contact support@mygoalapp.com.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-2">
              <a
                data-testid="organization-contact-open-mail"
                href="mailto:support@mygoalapp.com?subject=Organization%20account%20request"
                className="py-2.5 rounded-xl font-medium bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white text-center"
              >
                Open Mail App
              </a>
              <button
                data-testid="organization-contact-copy-email"
                onClick={handleCopyOrgEmail}
                className={`py-2.5 rounded-xl font-medium ${theme.button} ${theme.textPrimary}`}
              >
                Copy email
              </button>
              <button
                data-testid="organization-contact-ok"
                onClick={() => setShowOrgContactPopup(false)}
                className={`py-2.5 rounded-xl font-medium ${theme.button} ${theme.textPrimary}`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AccountSelectionPage;
